'use client'

import { useEffect, useState } from 'react'
import { m, AnimatePresence } from 'framer-motion'
import { type MediaItem } from '@/data/projects'

interface LightboxProps {
  items: MediaItem[]
  initialIndex: number
  open: boolean
  onClose: () => void
}

export default function Lightbox({ items, initialIndex, open, onClose }: LightboxProps) {
  const [current, setCurrent] = useState(initialIndex)

  useEffect(() => {
    if (open) setCurrent(initialIndex)
  }, [open, initialIndex])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') setCurrent(c => Math.max(0, c - 1))
      if (e.key === 'ArrowRight') setCurrent(c => Math.min(items.length - 1, c + 1))
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, items.length, onClose])

  const item = items[current]

  return (
    <AnimatePresence>
      {open && (
        <m.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <m.div
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Media */}
          <m.div
            key={current}
            className="relative z-10 max-w-5xl w-full"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {item.type === 'video' ? (
              <video
                src={item.src}
                poster={item.poster}
                controls
                className="w-full rounded-lg max-h-[80vh] object-contain"
              />
            ) : item.type === 'youtube' ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`${item.src}?autoplay=1&rel=0`}
                  className="absolute inset-0 w-full h-full rounded-lg"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              </div>
            ) : (
              <img
                src={item.src}
                alt={item.caption ?? `Slide ${current + 1}`}
                className="w-full rounded-lg max-h-[80vh] object-contain"
              />
            )}

            {/* Caption */}
            {item.caption && (
              <p className="text-center font-mono text-sm text-white/60 mt-3">{item.caption}</p>
            )}

            {/* Counter */}
            <p className="text-center font-mono text-xs text-white/40 mt-1">
              {current + 1} / {items.length}
            </p>
          </m.div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            ✕
          </button>

          {/* Prev */}
          {current > 0 && (
            <m.button
              onClick={() => setCurrent(c => c - 1)}
              className="absolute left-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-batman-gold/30 flex items-center justify-center text-white transition-colors"
              whileHover={{ scale: 1.1 }}
            >
              ←
            </m.button>
          )}

          {/* Next */}
          {current < items.length - 1 && (
            <m.button
              onClick={() => setCurrent(c => c + 1)}
              className="absolute right-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-batman-gold/30 flex items-center justify-center text-white transition-colors"
              whileHover={{ scale: 1.1 }}
            >
              →
            </m.button>
          )}
        </m.div>
      )}
    </AnimatePresence>
  )
}
